import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { useTable, appTableFeatures, ColumnDef } from '@/lib/tanstack-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
} from 'src/components/ui/table';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Checkbox } from 'src/components/ui/checkbox';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Button } from 'src/components/ui/button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from 'src/components/ui/tooltip';
import { CaretDownIcon } from '@phosphor-icons/react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Modal from 'src/components/modal/Modal';
import ProductV2Pill from 'src/components/products/ProductV2Pill';
import SalesOrderDraftModal from 'src/pages/authenticated/sales-orders/components/sales-order-draft-modal/SalesOrderDraftModal';
import CustomerPill from 'src/components/customer/CustomerPill';
import { SalesOrderDTO } from 'src/api/accounts-receivable/sales-orders/useSalesOrder';
import { SalesOrderSource } from '@erp/shared-types';
import useUpdateSalesOrderLineItem from 'src/api/accounts-receivable/sales-orders/useUpdateSalesOrderLineItem';
import * as Sentry from '@sentry/react';
import useRetrySendEdiAck from 'src/api/accounts-receivable/sales-orders/useRetrySendEdiAck';
import ApiError from 'src/api/ApiError';
import { Badge } from 'src/components/ui/badge';
import { Num, T, useGT, Var } from 'gt-react';

const EN_US_LOCALE = 'en-US';

const USD_TWO_DECIMAL_FORMATTER = new Intl.NumberFormat(EN_US_LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsdAmount(value: number): string {
  return `$${USD_TWO_DECIMAL_FORMATTER.format(value)}`;
}

// Toggle this flag to show the ERP sync status view instead of the normal
// sales order view.
const SHOW_FAMOUS_SYNC = true;

/** Display name of the ERP this order syncs to, from its originSystem. */
const ERP_DISPLAY_NAMES: Record<string, string> = {
  famous: 'Famous',
  'oracle-ebs': 'Oracle EBS',
  'oracle-fusion': 'Oracle Fusion',
};

function erpDisplayName(originSystem: string | null | undefined): string {
  return (originSystem && ERP_DISPLAY_NAMES[originSystem]) || 'ERP';
}

type SalesOrderLineItem = SalesOrderDTO['salesOrderLineItems'][number];

interface Props {
  salesOrder: SalesOrderDTO;
  editable?: boolean;
  onUpdate?: () => void;
}

// Edit field modal
interface EditFieldModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (value: string | number, sendAcknowledgment: boolean) => void;
  lineItem: SalesOrderLineItem;
  field: 'quantity' | 'unitPrice' | 'rescheduledDate';
  currentValue: string | number | null;
  isLoading?: boolean;
  error?: ApiError | null;
  salesOrder: SalesOrderDTO;
}

const EditFieldModal: React.FC<EditFieldModalProps> = ({
  open,
  onClose,
  onConfirm,
  lineItem,
  field,
  currentValue,
  isLoading = false,
  error,
  salesOrder,
}) => {
  const gt = useGT();
  const [value, setValue] = useState('');
  const [sendAcknowledgment, setSendAcknowledgment] = useState(true);

  useEffect(() => {
    if (open) {
      if (
        field === 'rescheduledDate' &&
        currentValue &&
        typeof currentValue === 'string'
      ) {
        // For date fields, format as YYYY-MM-DD
        setValue(currentValue.split('T')[0]);
      } else {
        setValue(currentValue?.toString() || '');
      }
    }
  }, [open, currentValue, field]);

  const handleConfirm = () => {
    let processedValue: string | number = value;

    if (field === 'quantity' || field === 'unitPrice') {
      processedValue = parseFloat(value);
      if (isNaN(processedValue) || processedValue <= 0) {
        alert(
          gt('Please enter a valid positive number', {
            context: 'validation when editing sales order line item',
          }),
        );
        return;
      }
    } else if (field === 'rescheduledDate') {
      if (!value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        alert(
          gt('Please enter a valid date (YYYY-MM-DD)', {
            context: 'validation date format',
          }),
        );
        return;
      }
    }

    onConfirm(processedValue, sendAcknowledgment);
  };

  const getFieldLabel = () => {
    switch (field) {
      case 'quantity':
        return gt('Quantity', { context: 'sales order line item field' });
      case 'unitPrice':
        return gt('Unit Price', { context: 'sales order line item field' });
      case 'rescheduledDate':
        return gt('Delivery Date', { context: 'sales order line item field' });
      default:
        return field;
    }
  };

  const getInputType = () => {
    switch (field) {
      case 'quantity':
        return 'number';
      case 'unitPrice':
        return 'number';
      case 'rescheduledDate':
        return 'date';
      default:
        return 'text';
    }
  };

  // Determine acknowledgment method based on sales order source (exhaustive check)
  const getAcknowledgmentMethod = (): string => {
    switch (salesOrder.source) {
      case SalesOrderSource.Edi:
        return 'EDI';
      case SalesOrderSource.Email:
      case SalesOrderSource.DirectEntry:
      case SalesOrderSource.FileUpload:
      case SalesOrderSource.ErpSync:
        return 'email';
      default: {
        // Exhaustiveness check - will cause TS error if a new source is added
        const exhaustiveCheck: never = salesOrder.source;
        const error = new Error(
          `Unhandled SalesOrderSource: ${exhaustiveCheck}`,
        );
        console.error(error);
        Sentry.captureException(error);
        return 'email'; // Fallback to email
      }
    }
  };

  return (
    <Modal
      open={open}
      title={`${gt('Edit', { context: 'edit modal title prefix' })} ${getFieldLabel()}`}
      closeable
      maxWidth="sm"
      isLoading={isLoading}
      error={error}
      onCloseButton={{
        text: gt('Cancel', { context: 'cancel button' }),
        onClick: onClose,
        disabled: isLoading,
      }}
      onConfirmButton={{
        text: isLoading
          ? gt('Saving...', { context: 'save in progress' })
          : gt('Save Changes', { context: 'save button' }),
        onClick: handleConfirm,
        disabled: isLoading,
      }}
    >
      <MDBox p={2}>
        <MDBox
          mb={3}
          p={2}
          sx={{
            backgroundColor: '#f0f4f8',
            borderRadius: 1,
            border: '1px solid #e1e8ed',
          }}
        >
          <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
            <T context="edit modal section heading">Editing Line Item:</T>
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={2}>
            {/* Product Info */}
            {lineItem.salesOrderItemType ===
              'customer_purchase_order_line_item' &&
            lineItem.customerPurchaseOrderLineItemId ? (
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  <T context="product linked to PO line">
                    Product from PO Line Item
                  </T>
                </MDTypography>
              </MDBox>
            ) : lineItem.productId && lineItem.productVariantId ? (
              <ProductV2Pill
                productId={lineItem.productId}
                productVariantId={lineItem.productVariantId}
              />
            ) : (
              <MDTypography variant="caption" color="text.secondary">
                <T context="missing product on line item">
                  Product info not available
                </T>
              </MDTypography>
            )}

            {/* Current values */}
            <MDBox display="flex" gap={3} ml="auto">
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  <T context="line item quantity label">
                    Qty:{' '}
                    <Var>
                      {lineItem.quantity != null
                        ? String(lineItem.quantity)
                        : '-'}
                    </Var>
                  </T>
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  <T context="line item unit price label">
                    Price:{' '}
                    <Var>
                      {lineItem.unitPrice != null
                        ? formatUsdAmount(Number(lineItem.unitPrice))
                        : '-'}
                    </Var>
                  </T>
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  <T context="line item delivery date label">
                    Delivery:{' '}
                    <Var>
                      {lineItem.rescheduledDate
                        ? new Date(
                            lineItem.rescheduledDate,
                          ).toLocaleDateString()
                        : '-'}
                    </Var>
                  </T>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>

        {error && (
          <AlertContainer variant="destructive" className="mb-2">
            <AlertDescription>
              <MDTypography variant="body2">
                <T context="error updating line item field">
                  Failed to update <Var>{getFieldLabel().toLowerCase()}</Var>:{' '}
                  <Var>
                    {error.message ||
                      gt('An error occurred', {
                        context: 'generic error message',
                      })}
                  </Var>
                </T>
              </MDTypography>
            </AlertDescription>
          </AlertContainer>
        )}

        <div className="mt-4 w-full">
          <Label>{getFieldLabel()}</Label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            type={getInputType()}
            className="w-full mt-1"
            autoFocus
            disabled={isLoading}
            step={field === 'unitPrice' ? '0.01' : undefined}
            min={
              field === 'quantity' || field === 'unitPrice' ? '0.01' : undefined
            }
          />
        </div>

        <MDBox
          mt={3}
          p={2}
          sx={{ backgroundColor: '#f8f9fa', borderRadius: 1 }}
        >
          <MDBox display="flex" alignItems="flex-start" gap={1.5}>
            <Checkbox
              checked={sendAcknowledgment}
              onCheckedChange={(checked) =>
                setSendAcknowledgment(Boolean(checked))
              }
              disabled={isLoading}
              className="mt-0.5"
            />
            <MDBox flex={1}>
              <MDTypography variant="body2" fontWeight="bold">
                <T context="checkbox label, notify customer of change">
                  Send customer acknowledgment
                </T>
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                <T context="explain customer acknowledgment channel">
                  Notify the customer about this change via{' '}
                  <Var>{getAcknowledgmentMethod()}</Var>
                </T>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
};

// Clickable Cell Component - just shows value and opens modal on click
const ClickableCell = ({
  value,
  onClick,
  type = 'number',
  prefix = '',
}: {
  value: number | string | null;
  onClick: () => void;
  type?: 'number' | 'text' | 'date';
  prefix?: string;
}) => {
  const formatValue = (val: number | string | null) => {
    if (type === 'number' && val !== null && val !== '') {
      const numVal = typeof val === 'string' ? parseFloat(val) : val;
      return !isNaN(numVal) ? numVal.toFixed(2) : val?.toString() || '';
    }
    if (type === 'date' && val) {
      return new Date(val + 'T12:00:00Z').toLocaleDateString();
    }
    return val?.toString() || '';
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      sx={{
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        minHeight: '24px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.3)',
        },
        border: '1px solid transparent',
      }}
      onClick={onClick}
    >
      <MDTypography variant="caption">
        {value ? (
          `${prefix}${formatValue(value)}`
        ) : (
          <T context="empty cell placeholder">-</T>
        )}
      </MDTypography>
    </MDBox>
  );
};

const SalesOrderView: React.FC<Props> = ({
  salesOrder,
  editable = false,
  onUpdate,
}) => {
  const gt = useGT();
  const [showStagedPurchaseOrderModal, setShowStagedPurchaseOrderModal] =
    useState(false);
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingField, setEditingField] = useState<{
    lineItem: SalesOrderLineItem;
    field: 'quantity' | 'unitPrice' | 'rescheduledDate';
    currentValue: string | number | null;
  } | null>(null);

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const [_retryEdiAckResult, setRetryEdiAckResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const salesOrderDraftId = salesOrder.stagedCustomerPurchaseOrderId;

  const updateSalesOrderLineItem = useUpdateSalesOrderLineItem(
    salesOrder.id,
    () => {
      console.log('Update successful');
      onUpdate?.();
      setShowEditModal(false);
      setEditingField(null);
    },
  );

  const retryEdiAckMutation = useRetrySendEdiAck(salesOrder.id, (response) => {
    setRetryEdiAckResult(response);
  });

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _handleRetrySendEdiAck = () => {
    setRetryEdiAckResult(null);
    retryEdiAckMutation.mutate();
  };

  const handleFieldClick = useCallback(
    (
      lineItem: SalesOrderLineItem,
      field: 'quantity' | 'unitPrice' | 'rescheduledDate',
      currentValue: string | number | null,
    ) => {
      setEditingField({ lineItem, field, currentValue });
      setShowEditModal(true);
    },
    [],
  );

  const handleEditConfirm = (
    value: string | number,
    sendAcknowledgment: boolean,
  ) => {
    if (!editingField) return;

    console.log('Making API call with:', {
      lineItemId: editingField.lineItem.id,
      field: editingField.field,
      value,
      sendAcknowledgment,
    });

    updateSalesOrderLineItem.mutate(
      {
        lineItemId: editingField.lineItem.id,
        field: editingField.field,
        value,
        sendAcknowledgment,
      },
      {
        onError: (error) => {
          console.error('Failed to update sales order line item:', error);
          // Don't close the modal on error, let the user see the error and try again
        },
      },
    );
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingField(null);
  };

  const lineItemColumns: ColumnDef<SalesOrderLineItem>[] = useMemo(
    () => [
      {
        accessorKey: 'productInfo',
        header: gt('Product', { context: 'line items table column' }),
        cell: (info) => {
          const lineItem = info.row.original;

          // Handle customer purchase order line item type
          if (
            lineItem.salesOrderItemType ===
              'customer_purchase_order_line_item' &&
            lineItem.customerPurchaseOrderLineItemId
          ) {
            return (
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  <T context="product linked to PO line">
                    Product from PO Line Item
                  </T>
                </MDTypography>
              </MDBox>
            );
          }

          // Handle direct entry type with product info
          if (lineItem.productId && lineItem.productVariantId) {
            return (
              <ProductV2Pill
                productId={lineItem.productId}
                productVariantId={lineItem.productVariantId}
              />
            );
          }

          return (
            <MDTypography variant="caption" color="text.secondary">
              <T context="missing product on line item">
                Product info not available
              </T>
            </MDTypography>
          );
        },
        minSize: 200,
      },
      {
        accessorKey: 'quantity',
        header: gt('Quantity', { context: 'line items table column' }),
        cell: (info) => {
          const value = info.getValue() as number | null;
          const lineItem = info.row.original;

          if (editable) {
            return (
              <ClickableCell
                value={value}
                onClick={() => handleFieldClick(lineItem, 'quantity', value)}
                type="number"
              />
            );
          }

          const supplierQty = lineItem.supplierQuantity;
          if (
            supplierQty != null &&
            (value == null || Number(supplierQty) !== Number(value))
          ) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <MDTypography
                    variant="caption"
                    sx={{
                      cursor: 'help',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textDecorationColor: 'rgba(0,0,0,0.3)',
                    }}
                  >
                    {supplierQty.toString()}
                  </MDTypography>
                </TooltipTrigger>
                <TooltipContent>
                  <>
                    <T>Finalized shipped quantity</T>
                    {' — '}
                    <T>
                      From fulfillment records (AR_TRX_LINE.QNT) — SO-stage qty:{' '}
                      <Var>{value ?? '—'}</Var>
                    </T>
                  </>
                </TooltipContent>
              </Tooltip>
            );
          }

          return (
            <MDTypography variant="caption">
              {value !== null ? (
                value.toString()
              ) : (
                <T context="empty quantity">-</T>
              )}
            </MDTypography>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'unitPrice',
        header: gt('Sale Price', { context: 'line items table column' }),
        cell: (info) => {
          const value = info.getValue() as number | null;
          const lineItem = info.row.original;

          if (editable) {
            return (
              <ClickableCell
                value={value}
                onClick={() => handleFieldClick(lineItem, 'unitPrice', value)}
                type="number"
                prefix="$"
              />
            );
          }

          return value !== null ? (
            <MDTypography variant="caption">
              {formatUsdAmount(value)}
            </MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              <T context="empty unit price">-</T>
            </MDTypography>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'supplierCostPrice',
        header: gt('Supplier Cost', { context: 'line items table column' }),
        cell: (info) => {
          const value = info.getValue() as number | null;
          const lineItem = info.row.original;
          const costPrice = lineItem.costPrice;

          let costDisplay: ReactNode;
          if (value != null) {
            costDisplay = (
              <Tooltip>
                <TooltipTrigger asChild>
                  <MDTypography
                    variant="caption"
                    sx={{
                      cursor: 'help',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textDecorationColor: 'rgba(0,0,0,0.3)',
                    }}
                  >
                    {formatUsdAmount(value)}
                  </MDTypography>
                </TooltipTrigger>
                <TooltipContent>
                  <T context="tooltip explaining finalized supplier cost source">
                    Finalized supplier cost from fulfillment records
                  </T>
                </TooltipContent>
              </Tooltip>
            );
          } else if (costPrice != null) {
            costDisplay = (
              <Tooltip>
                <TooltipTrigger asChild>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      cursor: 'help',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textDecorationColor: 'rgba(0,0,0,0.2)',
                    }}
                  >
                    {formatUsdAmount(costPrice)}
                  </MDTypography>
                </TooltipTrigger>
                <TooltipContent>
                  <T context="tooltip explaining estimated cost not yet finalized">
                    Estimated cost (SO-stage) — not yet finalized
                  </T>
                </TooltipContent>
              </Tooltip>
            );
          } else {
            costDisplay = (
              <MDTypography variant="caption" color="text.secondary">
                <T context="empty supplier cost">-</T>
              </MDTypography>
            );
          }

          return (
            <MDBox display="flex" alignItems="center" gap={0.5}>
              {costDisplay}
              {lineItem.isConsignment && (
                <Badge
                  variant="outline"
                  className="h-[18px] text-[0.6rem] font-bold px-1"
                >
                  C
                </Badge>
              )}
            </MDBox>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'lineTotal',
        header: gt('Total', { context: 'line items table column' }),
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <MDTypography variant="caption">
              {formatUsdAmount(value)}
            </MDTypography>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'rescheduledDate',
        header: gt('Delivery Date', { context: 'line items table column' }),
        cell: (info) => {
          const value = info.getValue() as string | null;
          const lineItem = info.row.original;

          if (editable) {
            return (
              <ClickableCell
                value={value}
                onClick={() =>
                  handleFieldClick(lineItem, 'rescheduledDate', value)
                }
                type="date"
              />
            );
          }

          return value ? (
            <MDTypography variant="caption">
              {new Date(value).toLocaleDateString()}
            </MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              <T context="empty delivery date">-</T>
            </MDTypography>
          );
        },
        size: 120,
      },
    ],
    [editable, gt, handleFieldClick],
  );

  const table = useTable({
    features: appTableFeatures,
    data: salesOrder.salesOrderLineItems || [],
    columns: lineItemColumns,
  });

  // Show the ERP sync status view if the flag is enabled. Branding follows
  // the order's actual target ERP (Famous / Oracle EBS / Oracle Fusion).
  const erpName = erpDisplayName(salesOrder.originSystem);
  if (SHOW_FAMOUS_SYNC) {
    // Special error view for sync failures
    if (salesOrder.originSyncStatus === 'failed') {
      return (
        <MDBox sx={{ p: 4 }}>
          <MDBox sx={{ maxWidth: '600px', margin: '0 auto' }}>
            <MDBox
              sx={{
                p: 4,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'error.main',
                backgroundColor: 'background.paper',
                maxWidth: '600px',
                width: '100%',
              }}
            >
              <MDTypography
                variant="h4"
                fontWeight="bold"
                mb={2}
                color="error.main"
              >
                <T context="error title, ERP sync failed">
                  ⚠️ <Var>{erpName}</Var> Sync Error
                </T>
              </MDTypography>

              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2}
                mb={3}
              >
                <MDBox
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                  }}
                />
                <MDTypography
                  variant="h6"
                  color="error.main"
                  fontWeight="medium"
                >
                  <T context="Famous sync failed heading">Sync Failed</T>
                </MDTypography>
              </MDBox>

              {salesOrder.originSyncErrorMessage && (
                <AlertContainer variant="destructive" className="mb-3">
                  <AlertDescription>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setIsErrorExpanded(!isErrorExpanded)}
                    >
                      <MDTypography variant="body2">
                        <strong>
                          <T context="expandable error section label">
                            Error Details
                          </T>
                        </strong>{' '}
                        {isErrorExpanded ? (
                          <T context="collapse error details">
                            (click to collapse)
                          </T>
                        ) : (
                          <T context="expand error details">
                            (click to expand)
                          </T>
                        )}
                      </MDTypography>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={
                          isErrorExpanded
                            ? gt('Collapse error details', {
                                context: 'collapse sync error details section',
                              })
                            : gt('Expand error details', {
                                context: 'expand sync error details section',
                              })
                        }
                        aria-expanded={isErrorExpanded}
                        className={
                          isErrorExpanded
                            ? 'rotate-180 transition-transform duration-300'
                            : 'rotate-0 transition-transform duration-300'
                        }
                      >
                        <CaretDownIcon size={16} />
                      </Button>
                    </MDBox>
                    {isErrorExpanded && (
                      <MDTypography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'var(--font-mono)',
                          mt: 1,
                          p: 1,
                          borderRadius: 1,
                        }}
                      >
                        <T context="full Famous sync error from server">
                          <Var>{salesOrder.originSyncErrorMessage}</Var>
                        </T>
                      </MDTypography>
                    )}
                  </AlertDescription>
                </AlertContainer>
              )}

              <MDBox
                sx={{
                  p: 3,
                  backgroundColor: '#fff3cd',
                  borderRadius: 1,
                  border: '1px solid #ffeaa7',
                  mb: 3,
                }}
              >
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  <T context="support callout heading">🆘 Support Required</T>
                </MDTypography>
                <MDTypography variant="body1" mb={2}>
                  <T context="instructions when ERP sync fails">
                    There was an error syncing this sales order with{' '}
                    <Var>{erpName}</Var>. Please contact our support team
                    immediately:
                  </T>
                </MDTypography>

                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 1,
                    border: '1px solid #e1e8ed',
                    textAlign: 'left',
                  }}
                >
                  <MDTypography variant="body2" fontWeight="bold" mb={1}>
                    <T context="support contact email">
                      📧 Email: support@gently-ai.com
                    </T>
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold">
                    <T context="support phone number">
                      📞 Phone/text: 646-657-5539
                    </T>
                  </MDTypography>
                </MDBox>

                <MDTypography
                  variant="body2"
                  mt={2}
                  sx={{ fontStyle: 'italic' }}
                >
                  <T context="support ticket instructions with order id">
                    Please include the sales order ID #
                    <Var>{salesOrder.internalId}</Var> and share the link to
                    this page when contacting support.
                  </T>
                </MDTypography>
              </MDBox>

              {/* Order Summary for reference */}
              <MDBox textAlign="left">
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  <T context="order reference section title">Order Reference</T>
                </MDTypography>
                <MDBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '140px 1fr',
                    gap: 1,
                    '& > .label': {
                      fontWeight: 'bold',
                      fontSize: 14,
                    },
                  }}
                >
                  <MDTypography className="label">
                    <T context="sales order field label">Order ID:</T>
                  </MDTypography>
                  <MDTypography variant="body2">
                    {salesOrder.originId ? (
                      <T context="external order id with hash">
                        #<Var>{salesOrder.originId}</Var>
                      </T>
                    ) : (
                      <T context="not applicable id">N/A</T>
                    )}
                  </MDTypography>

                  <MDTypography className="label">
                    <T context="sales order field label">Customer:</T>
                  </MDTypography>
                  <MDTypography variant="body2">
                    {salesOrder.customer?.name || (
                      <T context="customer name not specified">Not specified</T>
                    )}
                  </MDTypography>

                  {/* <MDTypography className="label">Total:</MDTypography>
                  <MDTypography variant="body2">
                    {salesOrder.totalAmount !== null
                      ? `$${new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(salesOrder.totalAmount)}`
                      : 'Not calculated'}
                  </MDTypography> */}
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    }

    // Normal sync status view for success/pending/other states
    const getSyncStatusColor = () => {
      switch (salesOrder.originSyncStatus) {
        case 'success':
          return 'success.main';
        case 'pending':
          return 'warning.main';
        default:
          return 'text.secondary';
      }
    };

    const getSyncStatusLabel = () => {
      switch (salesOrder.originSyncStatus) {
        case 'success':
          return gt('Successfully Synced', {
            context: 'Famous ERP integration sync status',
          });
        case 'pending':
          return gt('Sync Pending', {
            context: 'Famous ERP integration sync status',
          });
        default:
          return gt('Not Synced', {
            context: 'Famous ERP integration sync status',
          });
      }
    };

    return (
      <MDBox sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <MDBox
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'center',
            p: 4,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* Famous Sync Status */}
          <MDBox
            sx={{
              p: 4,
              borderRadius: 2,
              border: '2px solid',
              borderColor: getSyncStatusColor(),
              backgroundColor: 'background.paper',
              maxWidth: '600px',
              width: '100%',
            }}
          >
            <MDTypography variant="h4" fontWeight="bold" mb={2}>
              <T context="ERP integration status heading">
                <Var>{erpName}</Var> Integration Status
              </T>
            </MDTypography>

            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              mb={3}
            >
              <MDBox
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: getSyncStatusColor(),
                }}
              />
              <MDTypography
                variant="h6"
                sx={{ color: getSyncStatusColor() }}
                fontWeight="medium"
              >
                {getSyncStatusLabel()}
              </MDTypography>
            </MDBox>

            <MDBox
              sx={{
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e1e8ed',
              }}
            >
              <MDTypography variant="h6" fontWeight="bold" mb={2}>
                <T context="action required heading after Famous sync">
                  📋 Action Required
                </T>
              </MDTypography>
              <MDTypography variant="body1" mb={2}>
                <T context="instructions to verify order in the ERP">
                  Please check <Var>{erpName}</Var> to confirm that this sales
                  order was created properly:
                </T>
              </MDTypography>
              <MDBox component="ul" sx={{ pl: 3, mb: 2 }}>
                <MDBox component="li" mb={1}>
                  <MDTypography variant="body2">
                    <T context="checklist item for Famous verification">
                      Verify all line items are present with correct quantities
                      and prices
                    </T>
                  </MDTypography>
                </MDBox>
                <MDBox component="li" mb={1}>
                  <MDTypography variant="body2">
                    <T context="checklist item for Famous verification">
                      Confirm customer information is accurate
                    </T>
                  </MDTypography>
                </MDBox>
                <MDBox component="li" mb={1}>
                  <MDTypography variant="body2">
                    <T context="checklist item for Famous verification">
                      Check shipping and delivery dates
                    </T>
                  </MDTypography>
                </MDBox>
                <MDBox component="li">
                  <MDTypography variant="body2">
                    <T context="checklist item for Famous verification">
                      Anything else you manually input into the order
                    </T>
                  </MDTypography>
                </MDBox>
              </MDBox>

              {salesOrder.originSyncStatus === 'success' && (
                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: '#e8f5e9',
                    borderRadius: 1,
                    border: '1px solid #4caf50',
                  }}
                >
                  <MDTypography variant="body2" color="success.dark">
                    <T context="success message ERP sync complete">
                      ✅ Order has been successfully synchronized with{' '}
                      <Var>{erpName}</Var>
                    </T>
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            {/* Sales Order Details Summary */}
            <MDBox mt={4} textAlign="left">
              <MDTypography variant="h6" fontWeight="bold" mb={2}>
                <T context="order summary section title">Order Summary</T>
              </MDTypography>
              <MDBox
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr',
                  gap: 1,
                  '& > .label': {
                    fontWeight: 'bold',
                    fontSize: 14,
                  },
                }}
              >
                <MDTypography className="label">
                  <T context="sales order field label">Gently Order ID:</T>
                </MDTypography>
                <MDTypography variant="body2">
                  <T context="internal order id with hash">
                    #<Var>{salesOrder.internalId}</Var>
                  </T>
                </MDTypography>

                <MDTypography className="label">
                  <T context="sales order field label">
                    <Var>{erpName}</Var> Order ID:
                  </T>
                </MDTypography>
                <MDTypography variant="body2">
                  <b>
                    {salesOrder.originId ? (
                      <Var>{salesOrder.originId}</Var>
                    ) : (
                      <T context="not applicable id">N/A</T>
                    )}
                  </b>
                </MDTypography>

                <MDTypography className="label">
                  <T context="sales order field label">Customer:</T>
                </MDTypography>
                <MDTypography variant="body2">
                  {salesOrder.customer?.name || (
                    <T context="customer name not specified">Not specified</T>
                  )}
                </MDTypography>

                {/* <MDTypography className="label">Total:</MDTypography>
                <MDTypography variant="body2">
                  {salesOrder.totalAmount !== null
                    ? `$${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(salesOrder.totalAmount)}`
                    : 'Not calculated'}
                </MDTypography> */}
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header section with key information */}
      <MDBox mb={3} sx={{ flexShrink: 0 }}>
        {salesOrderDraftId && (
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDTypography
              sx={{
                fontWeight: 'bold',
                fontSize: 14,
                cursor: 'pointer',
                color: 'primary.main',
                textDecoration: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  color: 'primary.dark',
                },
              }}
              onClick={() => {
                setShowStagedPurchaseOrderModal(true);
              }}
            >
              <T context="link to open staged purchase order">
                See original purchase order
              </T>
            </MDTypography>
          </MDBox>
        )}

        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            gap: 1,
            '& > .label': {
              fontWeight: 'bold',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          <MDTypography className="label">
            <T context="sales order field label">Customer:</T>
          </MDTypography>
          {salesOrder.customer ? (
            <CustomerPill
              customer={{
                name: salesOrder.customer.name,
                erpDisplayName: salesOrder.customer.erpDisplayName,
                emailDomain: salesOrder.customer.emailDomain ?? undefined,
                phoneNumber: salesOrder.customer.phoneNumber ?? undefined,
              }}
              showERPDisplayName
            />
          ) : (
            <MDTypography variant="body2" color="text.secondary">
              <T context="customer name not specified">Not specified</T>
            </MDTypography>
          )}

          <MDTypography className="label">
            <T context="sales order field label">Ship To Location:</T>
          </MDTypography>
          <MDTypography variant="body2">
            {salesOrder.shipToLocation?.address}
          </MDTypography>

          <MDTypography className="label">
            <T context="sales order field label">Order Date:</T>
          </MDTypography>
          <MDTypography variant="body2">
            {new Date(salesOrder.orderDate).toLocaleDateString()}
          </MDTypography>

          <MDTypography className="label">
            <T context="sales order field label">Delivery Date:</T>
          </MDTypography>
          <MDTypography variant="body2">
            {salesOrder.deliveryDate ? (
              new Date(salesOrder.deliveryDate).toLocaleDateString()
            ) : (
              <T context="delivery date not set">Not specified</T>
            )}
          </MDTypography>

          <MDTypography className="label">
            <T context="sales order field label">Status:</T>
          </MDTypography>
          <MDTypography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {salesOrder.status.replace('_', ' ')}
          </MDTypography>

          <MDTypography className="label">
            <T context="sales order field label">Total:</T>
          </MDTypography>
          <MDTypography variant="body2">
            {salesOrder.totalAmount !== null ? (
              formatUsdAmount(salesOrder?.totalAmount ?? 0)
            ) : (
              <T context="order total not calculated">Not calculated</T>
            )}
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* Line items table */}
      <MDBox sx={{ flex: 1, minHeight: 0 }}>
        <MDBox
          mb={2}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            pb: 1,
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" fontWeight="bold">
              <T context="line items section heading with count">
                Line Items (
                <Num>{salesOrder.salesOrderLineItems?.length || 0}</Num>)
              </T>
            </MDTypography>
          </MDBox>
        </MDBox>

        <div className="overflow-x-auto">
          <Table style={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHeader>
              <TableRow>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header, index) => {
                    const columnWidths = [
                      '28%',
                      '12%',
                      '14%',
                      '16%',
                      '14%',
                      '16%',
                    ];

                    return (
                      <TableHead
                        key={header.id}
                        className="text-left opacity-70 text-[rgb(123,128,154)] text-[0.65rem] font-bold uppercase whitespace-nowrap overflow-hidden text-ellipsis"
                        style={{ width: columnWidths[index] }}
                      >
                        {header.isPlaceholder
                          ? null
                          : header.column.columnDef.header instanceof Function
                            ? header.column.columnDef.header(
                                header.getContext(),
                              )
                            : header.column.columnDef.header}
                      </TableHead>
                    );
                  }),
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => {
                  const isRejected = row.original.ediRejected;
                  return (
                    <TableRow
                      key={row.id}
                      className={
                        isRejected
                          ? 'opacity-60 line-through bg-black/[0.02]'
                          : 'hover:bg-black/[0.04]'
                      }
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        const columnWidths = [
                          '28%',
                          '12%',
                          '14%',
                          '16%',
                          '14%',
                          '16%',
                        ];

                        return (
                          <TableCell
                            key={cell.id}
                            className={`text-[#7b809a] text-sm font-normal overflow-hidden text-ellipsis whitespace-nowrap${isRejected ? ' line-through opacity-70' : ''}`}
                            style={{ width: columnWidths[index] }}
                          >
                            {cell.column.columnDef.cell instanceof Function
                              ? cell.column.columnDef.cell(cell.getContext())
                              : String(cell.getValue() ?? '')}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={lineItemColumns.length}
                    className="text-center py-8 text-muted-foreground"
                  >
                    <MDTypography variant="body2">
                      <T context="empty sales order line items">
                        No line items found
                      </T>
                    </MDTypography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </MDBox>

      {showStagedPurchaseOrderModal && salesOrderDraftId && (
        <SalesOrderDraftModal
          salesOrderDraftId={salesOrderDraftId}
          onClose={() => setShowStagedPurchaseOrderModal(false)}
          editable={false}
          isLoading={false}
        />
      )}

      {showEditModal && editingField && (
        <EditFieldModal
          open={showEditModal}
          onClose={handleEditCancel}
          onConfirm={handleEditConfirm}
          lineItem={editingField.lineItem}
          field={editingField.field}
          currentValue={editingField.currentValue}
          isLoading={updateSalesOrderLineItem.isLoading}
          error={updateSalesOrderLineItem.error}
          salesOrder={salesOrder}
        />
      )}
    </MDBox>
  );
};

export default SalesOrderView;
